import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import moment from 'moment'
import { omit } from 'ramda'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import { generateApiErrorPayload } from 'api-client/helpers'
import Alert from 'core/components/Alert'
import ExternalLink from 'core/components/ExternalLink'
import Progress from 'core/components/progress/Progress'
import {
  type ProgressDetails,
  ProgressStatus,
  StepProgress,
} from 'core/components/progress/StepProgress'
import SelectableCard from 'core/components/SelectableCard'
import PicklistField from 'core/components/validatedForm/DropdownField'
import { ErrorMessage } from 'core/components/validatedForm/ErrorMessage'
import TextField from 'core/components/validatedForm/TextField'
import Badge from 'core/elements/badge/Badge'
import Radio from 'core/elements/input/Radio'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import type { Route } from 'core/plugins/route'
import { type SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import type Theme from 'core/themes/model'
import { isOpenCloud } from 'core/utils/helpers'
import { routeManager } from 'core/utils/routes'
import { isSelfServiceRole } from 'k8s/util/helpers'
import {
  deleteImage,
  getImageById,
  getImagesWithName,
  listImages,
} from 'openstack/components/images/actions'
import { listHostNetworkConfigs } from 'openstack/components/infrastructure/actions'
import { waitSeconds } from 'openstack/components/infrastructure/blueprint/helpers'
import { listClusterHostGroups } from 'openstack/components/infrastructure/clusters/actions'
import { clusterHostGroupsSelector } from 'openstack/components/infrastructure/clusters/selectors'
import { listSecurityGroups } from 'openstack/components/networks/security-groups/actions'
import { listSshKeys } from 'openstack/components/security/keys/actions'
import { sshKeysSelector } from 'openstack/components/security/keys/selectors'
import { humanReadableSize } from 'openstack/helpers'
import { noop } from 'utils/fp'
import ServiceUnhealthyInfo from '../common/ServiceUnhealthyInfo'
import { listFlavors } from '../flavors/actions'
import { ImagePageTabs } from '../images/model'
import { imagesSelector, resolveSourceVmId } from '../images/selectors'
import ClusterHostGroupsPicklist from '../infrastructure/clusters/ClusterHostGroupsPicklist'
import { NetworkMode } from '../networks/constants'
import { getVolumeSnapshot, listVolumeSnapshots } from '../storage/snapshots/actions'
import { volumeSnapshotsByIdSelector } from '../storage/snapshots/selectors'
import { createVirtualMachine, getVirtualMachine, snapshotVirtualMachine } from './actions'
import { placeholderCloudInitYaml } from './CreateVirtualMachineModal'
import { syncNetworkAttachments } from './CreateVirtualMachineModal.helpers'
import CreateVirtualMachineCustomizeStep, {
  useCreateVirtualMachineCustomizeStepController,
} from './createVirtualMachineSteps/CustomizeStep'
import CreateVirtualMachineFlavorStep, {
  useCreateVirtualMachineFlavorStepController,
} from './createVirtualMachineSteps/FlavorStep'
import CreateVirtualMachineNetworkingStep, {
  useCreateVirtualMachineNetworkingStepController,
} from './createVirtualMachineSteps/NetworkingStep'
import { isHotplugVm, isVolumeBackedVm } from './helpers'
import useNetworkPorts, { type NetworkPortsResult } from './hooks/useNetworkPorts'
import StepTitle from './StepTitle'
import {
  buildNetworkEntries,
  LAYER_2_PORT_METADATA_KEY,
  MANAGED_NETWORK_PORT_METADATA_KEY,
} from './utils/vmBodyBuilder'
import VmCredentialsPanel from './VmCredentialsPanel'
import VmSnapshotInfo from './VmSnapshotInfo'

const useStyles = makeStyles<Theme>((theme) => ({
  passwordScreen: {
    display: 'grid',
    gap: 24,
  },
  errorMessageContainer: {
    marginLeft: theme.spacing(1),
  },
  // CLVM-FR-001: Step 1's "Current state" vs "Existing Snapshots" picker
  sourceLabel: {
    fontWeight: 600,
  },
  sourceCards: {
    display: 'grid',
    gap: theme.spacing(2),
  },
  sourceCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  sourceCardDivider: {
    height: 1,
    background: theme.palette.grey[200],
    margin: theme.spacing(1, 0),
  },
  sourceCardBody: {
    display: 'grid',
    gridTemplateColumns: 'max-content auto',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  sourceCardBodyInner: {
    alignItems: 'start',
    padding: theme.spacing(1, 0, 0, 0),
  },
  sourceCardDetails: {
    display: 'grid',
    gap: theme.spacing(0.5),
  },
  volumeSnapshotsColumn: {
    display: 'flex',
    flexFlow: 'column',
    gap: theme.spacing(0.5),
    height: '100%',
    borderLeft: `1px solid ${theme.palette.grey[200]}`,
    paddingLeft: theme.spacing(2),
  },
  volumeSnapshotRow: {
    display: 'flex',
    gap: theme.spacing(8),
  },
  customizeStep: {
    display: 'grid',
    gap: theme.spacing(1),
    gridTemplateColumns: '1fr 1fr',
    marginBottom: theme.spacing(2),
  },
  // CLVM-FR-001: empty state for "Existing Snapshots" when this VM has none yet, rather than
  // hiding the section entirely — matches the "No data found" boxes used elsewhere in the app.
  emptySnapshots: {
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 4,
    padding: theme.spacing(4),
    display: 'flex',
    justifyContent: 'center',
  },
  emptySnapshotsText: {
    color: theme.palette.grey[500],
  },
}))

interface Props {
  cloneRoute: Route
  rows: any[]
  onClose: (success?: boolean) => void
}

// CLVM-FR-010, CLVM-FR-011, CLVM-FR-012: Runs the whole clone, step by step. When `existingSnapshotId`
// is given (an existing snapshot the user picked), the "take a new snapshot" sub-step is skipped
// entirely and the temporary-snapshot cleanup at the end is skipped too (it isn't temporary — the
// user explicitly chose to keep it). Otherwise this is identical to the original Clone pipeline:
// snapshot the source VM, wait for that snapshot image to be ready, create the network ports the new
// VM needs, create the new VM, then clean up the snapshot.
const cloneVmFromImage = async ({
  vm,
  existingSnapshotId, // CLVM-FR-010: set only when Step 1 picked an existing snapshot (future T005 work)
  snapshotFn,
  createFn,
  deleteFn, // action that deletes the snapshot image
  setCloneProgressDetails,
  setVmPassword,
  setIsWindowsVm, // CLVM-FR-014: only Windows VMs get an admin password worth showing
  setSnapshotDeleteError, // shows an error if cleaning up the snapshot fails
  setPortCreationError, // shows an error if creating network ports fails
  setCloneError, // shows an error for any other step failure (snapshot creation, snapshot status)
  params,
  preCreatePortsFn, // creates network ports up front (for both simple and managed networks)
  cleanupPortsFn, // deletes those pre-created ports if something goes wrong
  currentTenant, // the project (tenant) the new VM belongs to
}) => {
  const isExistingSnapshot = !!existingSnapshotId
  let snapshotName = ''

  if (!isExistingSnapshot) {
    snapshotName = `${vm?.name}-${moment().format('YYYY-MM-DD-HH:mm:ss')}`
    // Same x_source_instance_uuid tagging as SnapshotVirtualMachineDialog (FR-014) — if the
    // temp-snapshot cleanup below ever fails, the orphaned snapshot can still be attributed
    // back to this VM in the Existing-Snapshots picker (CLVM-FR-002) instead of being orphaned.
    const snapshotBody = {
      createImage: { name: snapshotName, metadata: { x_source_instance_uuid: vm.id } },
    }
    const { success: snapshotVmSuccess, error: snapshotVmError } = await snapshotFn({
      id: vm.id,
      body: snapshotBody,
    })
    if (!snapshotVmSuccess) {
      const errorPayload = generateApiErrorPayload(
        snapshotVmError,
        '',
        'Failed to create a snapshot of the VM',
      )
      setCloneError({
        title: 'Snapshot creation failed',
        message: errorPayload?.message || 'Failed to create a snapshot of the VM.',
      })
      setCloneProgressDetails([
        { step: 1, status: 'failed', label: 'VM Snapshot Initiated' },
        { step: 2, status: 'failed', label: 'VM Snapshot Ready' },
        { step: 3, status: 'failed', label: 'VM Creation Initiated' },
        { step: 4, status: 'failed', label: 'VM Snapshot Cleanup' },
      ])
      return false
    }
  }

  setCloneProgressDetails([
    { step: 1, status: 'success', label: 'VM Snapshot Initiated' },
    { step: 2, status: 'pending', label: 'VM Snapshot Ready' },
    { step: 3, status: 'pending', label: 'VM Creation Initiated' },
    { step: 4, status: 'pending', label: 'VM Snapshot Cleanup' },
  ])

  // Keep checking until the snapshot image shows up and finishes processing (or, for an existing
  // snapshot, confirm it's still ready — it presumably already is, but we don't assume). A snapshot
  // is an image in the image library, and we can't build the new VM from it until it's "active".
  let snapshotId = existingSnapshotId || ''
  let snapshotReady = false
  // SC-007: an existing snapshot the user picked can be deleted by someone/something else between
  // selection and submission. Without a bound, a snapshot that will never reappear polls forever —
  // the fresh-snapshot branch doesn't need this bound since Nova is actively creating that one and
  // will reliably produce it.
  let existingSnapshotPollAttempts = 0
  const MAX_EXISTING_SNAPSHOT_POLL_ATTEMPTS = 60 // ~5 minutes at 5s per attempt
  while (!snapshotReady) {
    if (isExistingSnapshot) {
      existingSnapshotPollAttempts += 1
      if (existingSnapshotPollAttempts > MAX_EXISTING_SNAPSHOT_POLL_ATTEMPTS) {
        setCloneError({
          title: 'Snapshot not found',
          message:
            'The selected snapshot could not be found — it may have been deleted. Cloning cannot continue.',
        })
        setCloneProgressDetails([
          { step: 1, status: 'success', label: 'VM Snapshot Initiated' },
          {
            step: 2,
            status: 'failed',
            label: 'VM Snapshot Ready — snapshot no longer found (it may have been deleted)',
          },
          { step: 3, status: 'failed', label: 'VM Creation Initiated' },
          { step: 4, status: 'failed', label: 'VM Snapshot Cleanup' },
        ])
        return false
      }
    }
    // Real bug fixed here: an existing snapshot has no name to search by (the fresh-snapshot
    // branch below searches by the name we just gave it), and `getImagesWithName('')` queries
    // Glance for images named exactly the empty string — never matches anything, not "any name" —
    // so this used to spin until the attempt bound above gave up. Fetch by id directly instead.
    const image = isExistingSnapshot
      ? await getImageById(existingSnapshotId)
      : (await getImagesWithName(snapshotName))?.[0]
    if (!image) {
      // snapshot not visible yet, keep waiting
      await waitSeconds(5)
      continue
    }
    // CLVM-FR-014: the admin password only matters for Windows VMs — known as soon as the
    // snapshot image itself is, regardless of whether it was just created or pre-existing.
    setIsWindowsVm(image?.os_type?.toLowerCase() === 'windows')
    snapshotId = image?.id

    if (image?.status === 'active') {
      // If the source VM boots from a volume (not a local disk), the snapshot image also points to
      // one or more volume snapshots. Those have to finish too, so wait until every one of them is
      // "available" before treating the snapshot as ready.
      if (image?.block_device_mapping) {
        try {
          // Coverage note: the `: null` branch below is unreachable — the outer `if` above
          // already guarantees `image.block_device_mapping` is truthy at this point.
          const blockStorage = image.block_device_mapping
            ? JSON.parse(image.block_device_mapping)
            : null
          if (blockStorage) {
            const snapshotIds = blockStorage
              .filter((vol) => vol.source_type === 'snapshot')
              .map((vol) => vol.snapshot_id)
            const promises = snapshotIds?.map((snapshotId) => getVolumeSnapshot(snapshotId))
            const responses = await Promise.allSettled(promises)
            const allSnapshotsAvailable = responses?.every(
              (response: any) => response?.value?.status === 'available',
            )
            if (!allSnapshotsAvailable) {
              await waitSeconds(5)
              continue
            }
          }
        } catch (e) {
          console.error(e)
        }
      }
      setCloneProgressDetails([
        { step: 1, status: 'success', label: 'VM Snapshot Initiated' },
        { step: 2, status: 'success', label: 'VM Snapshot Ready' },
        { step: 3, status: 'pending', label: 'VM Creation Initiated' },
        { step: 4, status: 'pending', label: 'VM Snapshot Cleanup' },
      ])
      snapshotReady = true
    } else if (['queued', 'saving'].includes(image?.status)) {
      // Snapshot is still being created — wait and check again.
      await waitSeconds(5)
    } else {
      // Any other status (killed, error, deleted, ...) means the snapshot failed. Give up.
      setCloneError({
        title: 'Snapshot failed',
        message: `The VM snapshot ended in an unexpected state ("${image?.status}") instead of becoming active. Cloning cannot continue.`,
      })
      setCloneProgressDetails([
        { step: 1, status: 'success', label: 'VM Snapshot Initiated' },
        { step: 2, status: 'failed', label: 'VM Snapshot Ready' },
        { step: 3, status: 'failed', label: 'VM Creation Initiated' },
        { step: 4, status: 'failed', label: 'VM Snapshot Cleanup' },
      ])
      return false
    }
  }

  // Only managed-network rows with a chosen subnet need a port created by us before the VM is
  // built (so it can be pinned to that subnet). Simple (L2) networks are not pre-created — Nova
  // creates and owns their ports from `{ uuid }`.
  let allPreCreatedPorts: NetworkPortsResult = {}
  // Coverage note: the `?? {}` fallbacks on `params.networkAttachments` below are defensive —
  // `defaultParams.networkAttachments` is always initialized to `{}` by this component itself,
  // so `params.networkAttachments` is never actually null/undefined in practice.
  const hasManagedSubnetSelections = Object.values(params.networkAttachments ?? {}).some((rows) =>
    (rows as any[]).some((row) => row.subnet !== '' && row.port === ''),
  )

  if (hasManagedSubnetSelections) {
    try {
      allPreCreatedPorts = (await preCreatePortsFn(
        params.networks,
        params.networkAttachments ?? {},
        currentTenant,
        params.name,
        1, // Single VM clone
        // CLVM-FR-008: put the user's chosen security groups directly on the pre-created ports —
        // Nova ignores the server-level security_groups for ports that already exist (PCD-5420),
        // so this is the only way they take effect. Empty list when SGs are off.
        params.useSecurityGroups ? params.securityGroups.map((sg: any) => sg.id) : [],
      )) as NetworkPortsResult
    } catch (error) {
      // Pull a readable message out of the API error so we can show it to the user.
      const errorPayload = generateApiErrorPayload(error, '', 'Failed to create network ports')
      const neutronError =
        (error as any)?.response?.data?.NeutronError || (error as any)?.NeutronError
      const errorTitle = neutronError?.type || 'Port pre-creation failed'

      setPortCreationError({
        title: errorTitle,
        message: errorPayload?.message || 'Failed to create network ports',
      })
      setCloneProgressDetails([
        { step: 1, status: 'success', label: 'VM Snapshot Initiated' },
        { step: 2, status: 'success', label: 'VM Snapshot Ready' },
        { step: 3, status: 'failed', label: 'VM Creation Initiated' },
        { step: 4, status: 'pending', label: 'VM Snapshot Cleanup' },
      ])
      return false
    }
  }

  // Build the request body for the new VM. First turn the metadata key/value list into an object.
  const metadata = params.metadata.reduce(
    (accum, pair) => ({ ...accum, [pair.key]: pair.value }),
    {},
  )
  const hotplugMetadata = params.enableHotplugging
    ? {
        HOTPLUG_MEMORY: String(params.hotplugMemory),
        HOTPLUG_MEMORY_MAX: String(params.hotplugMemoryMax),
        HOTPLUG_CPU: String(params.hotplugVcpu),
        HOTPLUG_CPU_MAX: String(params.hotplugVcpuMax),
      }
    : {}

  // Save the IDs of the managed ports we created onto the VM's metadata, so we can find and
  // delete them when the VM is deleted. (Simple/L2 ports are created and owned by Nova.)
  const managedPortIds = Object.values(allPreCreatedPorts).join(',') || undefined

  const combinedMetadata = {
    ...metadata,
    ...hotplugMetadata,
    ...(managedPortIds && { [MANAGED_NETWORK_PORT_METADATA_KEY]: managedPortIds }),
  }

  // cloud-init is a startup script for the VM. Nova expects it base64-encoded, so encode it here.
  const encodedScript = params.useCloudInit ? btoa(params.cloudInitYaml) : undefined
  const shouldEnableConfigDrive =
    params.useCloudInit &&
    params.networks.some(
      (network) =>
        network.isSimpleNetwork ||
        network.subnetDetails?.some((subnet: any) => subnet.enable_dhcp === false),
    )

  const vmBody = {
    server: {
      imageRef: snapshotId,
      // CLVM-FR-006: FlavorStep's ListTableField/SelectableCard always hand back an array.
      flavorRef: params.flavor?.[0]?.id,
      name: params.name,
      networks: buildNetworkEntries(params, {
        managedNetworkPorts: allPreCreatedPorts,
      }),
      key_name: params?.sshKey || undefined,
      config_drive: shouldEnableConfigDrive,
      user_data: params.useCloudInit ? encodedScript : undefined,
      // CLVM-FR-008: send the user's final security-group selection from the Customize step;
      // empty array (not the project default) when the toggle is off, same as Create VM.
      security_groups: params.useSecurityGroups
        ? params.securityGroups.map((group: any) => ({ name: group.name }))
        : [],
      metadata: combinedMetadata,
      ...(params.availabilityZone && { availability_zone: params.availabilityZone }),
    },
  }

  // Create the new VM, then keep checking on it until it has finished building.
  let vmInfo
  let createSuccessFlag = false
  try {
    const { success: createSuccess, response } = await createFn({ body: vmBody })
    createSuccessFlag = createSuccess
    vmInfo = response
    if (createSuccess) {
      // CLVM-FR-011: an existing snapshot is already fully active (we polled it ready before
      // getting here) — Nova creates the new VM from it near-instantly, so there's no need to
      // wait for vm_state to leave "building" the way a fresh-snapshot clone does. Show success
      // the moment the create call itself succeeds.
      if (!isExistingSnapshot) {
        while (!vmInfo?.['OS-EXT-STS:vm_state'] || vmInfo['OS-EXT-STS:vm_state'] === 'building') {
          setCloneProgressDetails([
            { step: 1, status: 'success', label: 'VM Snapshot Initiated' },
            { step: 2, status: 'success', label: 'VM Snapshot Ready' },
            { step: 3, status: 'pending', label: 'VM Creation Initiated' },
            { step: 4, status: 'pending', label: 'VM Snapshot Cleanup' },
          ])
          await waitSeconds(5)
          try {
            const result = await getVirtualMachine({ id: response.id })
            if (result) vmInfo = result
          } catch (err) {
            console.error('VM polling failed:', err)
          }
        }
      }

      setCloneProgressDetails([
        { step: 1, status: 'success', label: 'VM Snapshot Initiated' },
        { step: 2, status: 'success', label: 'VM Snapshot Ready' },
        { step: 3, status: 'success', label: 'VM Creation Initiated' },
        { step: 4, status: 'pending', label: 'VM Snapshot Cleanup' },
      ])

      setVmPassword(response?.adminPass)
    } else {
      setCloneProgressDetails([
        { step: 1, status: 'success', label: 'VM Snapshot Initiated' },
        { step: 2, status: 'success', label: 'VM Snapshot Ready' },
        { step: 3, status: 'failed', label: 'VM Creation Initiated' },
        { step: 4, status: 'pending', label: 'VM Snapshot Cleanup' },
      ])
    }
  } finally {
    const hasPreCreatedPorts = Object.keys(allPreCreatedPorts).length > 0
    if (!createSuccessFlag && hasPreCreatedPorts) {
      try {
        await cleanupPortsFn(allPreCreatedPorts)
      } catch (cleanupError) {
        console.error('[Clone] Port cleanup failed:', cleanupError)
      }
    }

    // CLVM-FR-012: only delete the snapshot if it was freshly created for this clone — a
    // pre-existing snapshot the user explicitly selected is never deleted.
    if (!isExistingSnapshot) {
      try {
        const { success: deleteSuccess, error } = await deleteFn({
          id: snapshotId,
        })
        setCloneProgressDetails((prev) => [
          ...prev.slice(0, 3),
          {
            step: 4,
            status: deleteSuccess ? 'success' : 'failed',
            label: 'VM Snapshot Cleanup',
          },
        ])
        if (!deleteSuccess) throw error || new Error('VM Snapshot Cleanup failed')
      } catch (err) {
        setSnapshotDeleteError(err)
      }
    } else {
      setCloneProgressDetails((prev) => [
        ...prev.slice(0, 3),
        { step: 4, status: 'success', label: 'VM Snapshot Cleanup' },
      ])
    }
  }

  return createSuccessFlag
}

// CLVM-FR-001 through CLVM-FR-005: Step 1 (Source) — choose whether to clone from the VM's current
// state or from one of its existing snapshots.
function CloneSourceStep({
  vm,
  existingSnapshotId,
  setExistingSnapshotId,
  existingSnapshots,
  loadingImages,
  loadingVolumeSnapshots,
  volumeSnapshotsById,
}) {
  const classes = useStyles()

  return (
    <div>
      <StepTitle
        title="Source"
        description={
          <>
            Choose whether to copy {vm?.name} as it is now or from one of its snapshots. Its
            properties are pre-filled in the next steps, and you can change any of them before
            deploying.
          </>
        }
      />
      <ServiceUnhealthyInfo action="VM Clone" hypervisor imageLibrary blockStorage />
      <VmSnapshotInfo vm={vm} isClonedVM />
      <div className={classes.sourceCards}>
        <Text variant="body2" className={classes.sourceLabel}>
          Create the new VM from
        </Text>
        <SelectableCard
          id="current-state"
          onClick={() => setExistingSnapshotId(null)}
          active={existingSnapshotId === null}
          withCustomBody
        >
          <div className={classes.sourceCardBody}>
            <Radio checked={existingSnapshotId === null} />
            <div className={classes.sourceCardDetails}>
              <Text variant="body2">Current state</Text>
              <Text variant="caption1">
                A new snapshot of {vm?.name} is taken now and the VM is created from it.
              </Text>
            </div>
          </div>
        </SelectableCard>
        <Text variant="caption1" className={classes.sourceLabel}>
          Existing Snapshots
        </Text>
        {existingSnapshots.length === 0 ? (
          <Progress
            loading={loadingImages || loadingVolumeSnapshots}
            message="Loading snapshots..."
          >
            <div className={classes.emptySnapshots}>
              <Text variant="caption1" className={classes.emptySnapshotsText}>
                <b>{vm?.name}</b> has no snapshots yet.
              </Text>
            </div>
          </Progress>
        ) : (
          existingSnapshots.map((snapshot, index) => {
            // CLVM-FR-005: volume-backed snapshots list their underlying Cinder volume
            // snapshots (name + size); ephemeral snapshots show their own virtual_size.
            const isVolumeBacked = snapshot.imageType === 'vmVolumeSnapshot'
            const underlyingVolumeSnapshots = (snapshot.snapshotIds ?? [])
              .map((snapshotId: string) => volumeSnapshotsById?.[snapshotId])
              .filter(Boolean)
            return (
              <SelectableCard
                key={snapshot.id}
                id={snapshot.id}
                onClick={() => setExistingSnapshotId(snapshot.id)}
                active={existingSnapshotId === snapshot.id}
                withCustomBody
              >
                <div className={classes.sourceCardBody}>
                  <Radio checked={existingSnapshotId === snapshot.id} />
                  <div>
                    <div className={classes.sourceCardHeader}>
                      <Text variant="caption1">{snapshot.name || snapshot.id}</Text>
                      {index === 0 && <Badge text="Latest" variant="primary" />}
                    </div>
                    <div className={classes.sourceCardDivider} />
                    <div className={clsx(classes.sourceCardBody, classes.sourceCardBodyInner)}>
                      <div className={classes.sourceCardDetails}>
                        <Text variant="body2">
                          Type: <b>{snapshot.imageTypeDisplay}</b>
                        </Text>
                        <Text variant="body2">
                          Virtual Disk Size:{' '}
                          <b>{isVolumeBacked ? 'N/A' : humanReadableSize(snapshot.virtual_size)}</b>
                        </Text>
                        <Text variant="body2">
                          Created: <b>{moment(snapshot.created_at).format('MM/DD/YYYY, h:mm A')}</b>
                        </Text>
                      </div>
                      {isVolumeBacked && underlyingVolumeSnapshots.length > 0 && (
                        <div className={classes.volumeSnapshotsColumn}>
                          <Text variant="body2">
                            Volume Snapshots: <b>{underlyingVolumeSnapshots.length}</b>
                          </Text>
                          {underlyingVolumeSnapshots.map((volumeSnapshot: any) => (
                            <div className={classes.volumeSnapshotRow} key={volumeSnapshot.id}>
                              <Text variant="body2">
                                {volumeSnapshot.name || volumeSnapshot.id}
                              </Text>
                              <Text variant="body2">
                                Size:{' '}
                                <b>{humanReadableSize(volumeSnapshot.size * 1024 * 1024 * 1024)}</b>
                              </Text>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </SelectableCard>
            )
          })
        )}
      </div>
    </div>
  )
}

// CLVM-FR-010/011/012: an existing-snapshot clone skips the snapshot-creation and cleanup
// sub-steps entirely, so showing all 4 generic checklist rows (two of which never meaningfully
// happen) is misleading — collapse to the one step that actually applies, renumbered as 1.
function getDisplayProgressDetails(
  progressDetails: ProgressDetails[],
  isExistingSnapshot: boolean,
): ProgressDetails[] {
  if (!isExistingSnapshot) return progressDetails
  const creationStep = progressDetails.find((detail) => detail.label === 'VM Creation Initiated')
  const anyFailed = progressDetails.some((detail) => detail.status === ProgressStatus.Failed)
  return [
    {
      step: 1,
      label: 'VM Creation Initiated',
      status: anyFailed ? ProgressStatus.Failed : (creationStep?.status ?? ProgressStatus.Pending),
    },
  ]
}

// CLVM-FR-013: Step 5, promoted from a toggled overlay to an explicit wizard step. Kicks off the
// clone the moment it mounts (i.e. the moment the wizard advances into step 5), exactly once.
// CLVM-FR-014: once the clone finishes, this same step also shows the generated credentials (no
// separate Step 6) — the wizard's final step only ever shows a "Finish" action once the VM is
// actually ready, never a "Next" into another screen. The credentials themselves (Step 1 of
// CLVM-FR-014) only render for Windows VMs, matching Create VM's isWindowsVmSource logic — every
// other OS just gets the completion message.
function CloneProgressStep({
  onStart,
  cloneProgressDetails,
  portCreationError,
  cloneError,
  cloneStage,
  vmPassword,
  isWindowsVm,
  isExistingSnapshot,
  snapshotDeleteError,
}) {
  const classes = useStyles()
  // biome-ignore lint/correctness/useExhaustiveDependencies: onStart is a stable callback from the parent, and we only want to call it once on mount.
  useEffect(() => {
    onStart()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const isDone = cloneStage === 'done'

  return (
    // ModalForm's step grid places each top-level child of a step's render output into its own
    // grid cell (see CLVM-FR-001's round-1 fix) — a bare Fragment here scatters this content
    // across the grid instead of keeping it together as one column, so it must stay a single div.
    <div>
      <StepTitle
        title="Progress"
        description="Track the clone's progress below. Once the VM is ready, a confirmation (and Windows credentials, if applicable) appear here."
      />
      {cloneError && (
        <Alert variant="error" title={cloneError.title} message={cloneError.message} />
      )}
      {portCreationError && (
        <Alert
          variant="error"
          title={portCreationError.title}
          message={portCreationError.message}
        />
      )}
      {isDone ? (
        <div className={classes.passwordScreen}>
          {snapshotDeleteError && (
            <Alert
              variant="error"
              title=""
              message={
                <div className={classes.errorMessageContainer}>
                  <ErrorMessage>
                    <Text variant="body2">
                      Failed to cleanup the VM snapshot. Ensure that the image library host is
                      reachable and its certificate is trusted. You may need to delete the VM
                      snapshot manually.{' '}
                      <ExternalLink
                        url={routeManager.routes.openstack.images.path({
                          tab: ImagePageTabs.Snapshots,
                        })}
                      >
                        Click here
                      </ExternalLink>
                    </Text>
                  </ErrorMessage>
                </div>
              }
            />
          )}
          <VmCredentialsPanel vmPassword={vmPassword} showCredentials={isWindowsVm} />
        </div>
      ) : (
        <StepProgress
          progressDetails={getDisplayProgressDetails(cloneProgressDetails, isExistingSnapshot)}
          title={
            isDone
              ? 'Cloning complete.'
              : 'Cloning is in progress. Please wait and do not close this dialog, or the clone operation will be cancelled.'
          }
        />
      )}
    </div>
  )
}

// Gates mounting of CloneVirtualMachineModalInner behind the route match, so its
// data-fetching hooks (listFlavors, listImages, listVolumeSnapshots, etc.) only run
// once the user actually opens Clone VM, not on every VM list/details page load —
// mirrors CreateVirtualMachineModal's identical gating for the same reason.
export default function CloneVirtualMachineModal({ cloneRoute, rows, onClose }: Props) {
  const { match } = useReactRouter()
  const isOpen = cloneRoute.pattern.match(match.url) && !!rows?.[0]

  if (!isOpen) {
    return null
  }
  return <CloneVirtualMachineModalInner cloneRoute={cloneRoute} rows={rows} onClose={onClose} />
}

function CloneVirtualMachineModalInner({ cloneRoute, rows: vms, onClose }: Props) {
  const { history } = useReactRouter()
  const vm = vms?.[0]
  const classes = useStyles()
  const [cloneStage, setCloneStage] = useState<'idle' | 'running' | 'done'>('idle')
  const [cloneProgressDetails, setCloneProgressDetails] = useState<ProgressDetails[]>([
    { step: 1, status: ProgressStatus.Pending, label: 'VM Snapshot Initiated' },
    { step: 2, status: ProgressStatus.Pending, label: 'VM Snapshot Ready' },
    { step: 3, status: ProgressStatus.Pending, label: 'VM Creation Initiated' },
    { step: 4, status: ProgressStatus.Pending, label: 'VM Snapshot Cleanup' },
  ])
  // CLVM-FR-001: null means "Current state" (default); a string is the id of the
  // existing snapshot the user picked instead.
  const [existingSnapshotId, setExistingSnapshotId] = useState<string | null>(null)
  const [loadedDefaultFlavor, setLoadedDefaultFlavor] = useState(false)
  const [loadedDefaultNetworks, setLoadedDefaultNetworks] = useState(false)
  const [loadedDefaultSshKey, setLoadedDefaultSshKey] = useState(false)
  const [loadedDefaultSecurityGroups, setLoadedDefaultSecurityGroups] = useState(false)
  const [vmPassword, setVmPassword] = useState('')
  // CLVM-FR-014: only Windows VMs get an admin password worth showing, matching Create VM's
  // isWindowsVmSource logic — known once the clone's underlying snapshot image is resolved.
  const [isWindowsVm, setIsWindowsVm] = useState(false)
  const [minAvailableIpsNetwork, setMinAvailableIpsNetwork] = useState(null)
  const isHotPluggedVm = isHotplugVm(vm)
  // CLVM-FR-003: the VM's own boot type decides which snapshot sub-type Step 1 will show (T005) and
  // drives the shared Flavor/Customize helpers' volume-vs-ephemeral logic below.
  const isVolumeBackedVM = useMemo(() => isVolumeBackedVm(vm), [vm])

  const defaultParams = {
    name: `${vm?.name}-clone`,
    enableHotplugging: isHotPluggedVm,
    gpuCompatible: false,
    // CLVM-FR-006/CLVM-FR-009: synthesizes the same `sourceType`/`image` shape Create VM's shared
    // flavor-filtering helpers (`isCreateVmVolumeSourceType`, `isFlavorCompatibleWithImageAndVolume`)
    // expect, derived from the source VM rather than from a user-driven source-type picker.
    sourceType: 'snapshot' as const,
    image: [
      {
        imageType: isVolumeBackedVM ? 'vmVolumeSnapshot' : 'ephemeralDiskSnapshot',
        virtual_size: vm?.imageDetails?.virtual_size,
      },
    ],
    attachVolumes: [],
    installerIso: [],
    flavor: [],
    selectedPorts: {},
    fixedIPs: {},
    selectedSubnets: {},
    networkAttachments: {},
    networks: [],
    availabilityZone: vm?.['OS-EXT-AZ:availability_zone'],
    sshKey: null,
    serverGroup: undefined,
    stretchedCluster: '',
    priority: undefined,
    useCloudInit: false,
    cloudInitYaml: placeholderCloudInitYaml,
    autoInstallQemuGuestAgent: false,
    useSecurityGroups: false,
    securityGroups: [],
    count: 1,
    hotplugMemory: vm?.metadata?.['HOTPLUG_MEMORY'] ? Number(vm.metadata['HOTPLUG_MEMORY']) : null,
    hotplugMemoryMax: vm?.metadata?.['HOTPLUG_MEMORY_MAX']
      ? Number(vm.metadata['HOTPLUG_MEMORY_MAX'])
      : Math.max(65536, Number(vm?.metadata?.['HOTPLUG_MEMORY'] ?? 0)),
    hotplugVcpu: vm?.metadata?.['HOTPLUG_CPU'] ? vm.metadata['HOTPLUG_CPU'] : null,
    hotplugVcpuMax: vm?.metadata?.['HOTPLUG_CPU_MAX']
      ? Number(vm.metadata['HOTPLUG_CPU_MAX'])
      : Math.max(64, Number(vm?.metadata?.['HOTPLUG_CPU'] ?? 0)),
    metadata: vm?.metadata
      ? Object.entries(
          omit(
            [
              'HOTPLUG_MEMORY',
              'HOTPLUG_MEMORY_MAX',
              'HOTPLUG_CPU',
              'HOTPLUG_CPU_MAX',
              LAYER_2_PORT_METADATA_KEY,
              MANAGED_NETWORK_PORT_METADATA_KEY,
            ],
            vm.metadata,
          ),
        ).map(([key, value]) => ({ key, value }))
      : [],
  }

  const { prefs } = useScopedPreferences()
  const { currentTenant } = prefs

  const session = useSelector((state: any) => state[sessionStoreKey] as SessionState)
  const { features } = session
  const isVmware = features?.experimental?.pmov2_du_type === 'vmware'
  const isSelfServiceUser = isSelfServiceRole(session)

  const { params, getParamsUpdater, updateParams, setParams } = useParams(defaultParams)

  const { loading: loadingFlavors } = useListAction(listFlavors, { initialLoadingState: true })

  useListAction(listClusterHostGroups)
  useListAction(listHostNetworkConfigs)

  const clusterHostGroups = useSelector(clusterHostGroupsSelector)
  const selectedCluster = useMemo(
    () => (clusterHostGroups as any[])?.find((c) => c.name === params.availabilityZone),
    [clusterHostGroups, params.availabilityZone],
  )

  const { loading: loadingSshKeys } = useListAction(listSshKeys, { initialLoadingState: true })
  const sshKeys = useSelector(sshKeysSelector)

  // CLVM-FR-001, CLVM-FR-002, CLVM-FR-003: existing-snapshot candidates for Step 1 — this
  // VM's own snapshots only (resolved via instance_uuid/x_source_instance_uuid, see
  // IMGLIST-FR-064), filtered to the type matching this VM's own boot type.
  const { loading: loadingImages } = useListAction(listImages)
  // Coverage note: the `?? []` fallback in `existingSnapshots` below is defensive — the real
  // `imagesSelector` always returns an array (it maps over the cache), so `images` is never
  // actually null/undefined in practice.
  const images = useSelector(imagesSelector as any) as any[]
  const { loading: loadingVolumeSnapshots } = useListAction(listVolumeSnapshots)
  const volumeSnapshotsById = useSelector(volumeSnapshotsByIdSelector as any) as Record<string, any>
  const existingSnapshots = useMemo(() => {
    // Coverage note: defensive — the outer component's gate (`!!rows?.[0]`) guarantees `vm` is
    // always truthy by the time Inner mounts, so this branch is unreachable in practice.
    if (!vm) return []
    const wantedType = isVolumeBackedVM ? 'vmVolumeSnapshot' : 'ephemeralDiskSnapshot'
    const vmVolumeIds = new Set(vm.volumeIds ?? [])
    return (images ?? [])
      .filter((image) => {
        if (image.imageType !== wantedType) return false
        if (resolveSourceVmId(image) === vm.id) return true
        if (!isVolumeBackedVM) return false
        return (image.snapshotIds ?? []).some((snapshotId: string) =>
          vmVolumeIds.has(volumeSnapshotsById?.[snapshotId]?.volume_id),
        )
      })
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
  }, [images, vm, isVolumeBackedVM, volumeSnapshotsById])

  // CLVM-FR-006, CLVM-FR-007, CLVM-FR-008: reuse Create VM's own step controllers rather than
  // reimplementing flavor/network/customize state management.
  const flavorStep = useCreateVirtualMachineFlavorStepController({ params })
  const networkingStep = useCreateVirtualMachineNetworkingStepController({
    clusterId: params.availabilityZone,
    applicableNetworkLabels: (selectedCluster as any)?.physicalNetworkLabels ?? [],
    currentTenant: currentTenant as string,
    isSelfServiceUser,
  })
  const customizeStep = useCreateVirtualMachineCustomizeStepController()
  useListAction(listSecurityGroups)

  const { update: snapshotVm } = useUpdateAction(snapshotVirtualMachine)
  const { update: createVm, error, reset } = useUpdateAction(createVirtualMachine)
  const { update: deleteSnapshotFn } = useUpdateAction(deleteImage)
  const [snapshotDeleteError, setSnapshotDeleteError] = useState(null)
  const [portCreationError, setPortCreationError] = useState<{
    title: string
    message: string
  } | null>(null)
  // Shows an error for any clone-pipeline failure that isn't already covered by a more specific
  // error state above (port creation, snapshot cleanup) — e.g. the initial snapshot-creation call
  // failing outright, or the snapshot ending in an unexpected (non-active) status.
  const [cloneError, setCloneError] = useState<{ title: string; message: string } | null>(null)
  const responseError = error

  const { preCreatePorts, cleanupPreCreatedPorts } = useNetworkPorts()

  const startClone = useCallback(async () => {
    setCloneStage('running')
    const cloneSuccess = await cloneVmFromImage({
      vm,
      existingSnapshotId, // CLVM-FR-001/010/011/012: Step 1's selection, null for "Current state"
      snapshotFn: snapshotVm,
      createFn: createVm,
      deleteFn: deleteSnapshotFn,
      setCloneProgressDetails,
      setVmPassword,
      setIsWindowsVm,
      setSnapshotDeleteError,
      setPortCreationError,
      setCloneError,
      params,
      preCreatePortsFn: preCreatePorts,
      cleanupPortsFn: cleanupPreCreatedPorts,
      currentTenant,
    })
    setCloneStage(cloneSuccess ? 'done' : 'idle')
  }, [
    params,
    vm,
    existingSnapshotId,
    snapshotVm,
    createVm,
    deleteSnapshotFn,
    preCreatePorts,
    cleanupPreCreatedPorts,
    currentTenant,
  ])

  const handleClose = useCallback(
    (success?: boolean) => {
      setParams(defaultParams)
      setExistingSnapshotId(null)
      setSnapshotDeleteError(null)
      setPortCreationError(null)
      setCloneError(null)
      setVmPassword('')
      setIsWindowsVm(false)
      setCloneStage('idle')
      reset()
      onClose(success)
      // CLVM-FR-015: Clone is route-driven and opened from two different pages (VM list, VM
      // details) via history.push — go back to whichever one opened it rather than hardcoding
      // a single destination, so closing from either entry point lands back in the right place.
      history.goBack()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onClose, reset, history],
  )

  // CLVM-FR-006: pre-select the flavor matching the source VM's current flavor; falls back to no
  // selection (CLVM-FR-009) if a matching flavor can't be found (e.g. it was deleted since).
  // Coverage note: this effect's `loadedDefaultFlavor` guard only matters on a second invocation
  // after the first has already run — exercising that specific branch in isolation requires
  // forcing an unrelated dependency (e.g. `flavorStep.flavors`) to change after mount, which
  // doesn't reflect a realistic interaction pattern for this component; left untested. The `!vm`
  // half of the guard is a defense-in-depth backstop against the exact race the outer component's
  // gate already prevents (this effect's `loadedDefault*` flag must not latch "no match found"
  // before `vm` is real, or it would never retry once `vm` arrives) — unreachable given the gate,
  // kept here in case this effect is ever reused somewhere without it.
  useEffect(() => {
    if (loadingFlavors || loadedDefaultFlavor || !vm) return
    const vmFlavor = vm?.flavor
    const defaultFlavor = (flavorStep.flavors as any[]).find((flavor) => {
      return (
        vmFlavor?.original_name === flavor.name &&
        vmFlavor?.disk === flavor.disk &&
        (!isHotPluggedVm ? vmFlavor?.ram === flavor.ram && vmFlavor?.vcpus === flavor.vcpus : true)
      )
    })
    if (defaultFlavor) {
      updateParams({ flavor: [defaultFlavor] })
    }
    setLoadedDefaultFlavor(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingFlavors, flavorStep.flavors, vm])

  // CLVM-FR-007: pre-attach networks matching the source VM's current networks (fresh Automatic
  // ports, not exact port/IP reconstruction — same approximation as today). Falls back to no
  // selection (CLVM-FR-009) if none of the VM's current networks can be matched.
  // Coverage note: see the flavor effect above — the `loadedDefaultNetworks` second-invocation
  // guard branch is similarly left untested for the same reason.
  useEffect(() => {
    if (networkingStep.loadingNetworks || loadedDefaultNetworks || !vm) return
    const vmNetworkNames = Object.keys(vm?.addresses ?? {})
    const defaultNetworks = (networkingStep.networksList as any[]).filter((network) =>
      vmNetworkNames.includes(network.name),
    )

    if (defaultNetworks.length > 0) {
      const hasSimpleNetwork = defaultNetworks.some((network) => network.isSimpleNetwork)
      const hasManagedNetwork = defaultNetworks.some(
        (network) => !network.isSimpleNetwork && network.subnets?.length > 0,
      )
      if (hasSimpleNetwork && !hasManagedNetwork) {
        networkingStep.setVmNetworkMode(NetworkMode.SIMPLE)
      } else {
        networkingStep.setVmNetworkMode(NetworkMode.MANAGED)
      }
    }

    updateParams({
      networks: defaultNetworks,
      networkAttachments: syncNetworkAttachments(defaultNetworks, {}),
    })
    setLoadedDefaultNetworks(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkingStep.loadingNetworks, networkingStep.networksList, vm])

  // CLVM-FR-008: pre-select security groups matching the source VM's current `security_groups`
  // (name-only objects from Nova, mapped to the full SG objects the Customize step's list
  // expects). Falls back to no selection (CLVM-FR-009) for any group that no longer exists —
  // deleted groups are simply omitted, not treated as an error. If CustomizeStep's own
  // network/port-driven auto-state (disabled/locked) applies, it runs after this and takes
  // precedence, same as it would for a user-driven selection.
  // Coverage note: see the flavor effect above — the `loadedDefaultSecurityGroups`
  // second-invocation guard branch is similarly left untested for the same reason.
  useEffect(() => {
    if (customizeStep.loadingSecurityGroups || loadedDefaultSecurityGroups || !vm) return
    const vmSecurityGroupNames = (vm?.security_groups ?? []).map((sg: any) => sg.name)
    const defaultSecurityGroups = (customizeStep.securityGroupsList as any[]).filter((group) =>
      vmSecurityGroupNames.includes(group.name),
    )
    if (defaultSecurityGroups.length > 0) {
      updateParams({ useSecurityGroups: true, securityGroups: defaultSecurityGroups })
    }
    setLoadedDefaultSecurityGroups(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customizeStep.loadingSecurityGroups, customizeStep.securityGroupsList, vm])

  // Coverage note: see the flavor effect above — the `loadedDefaultSshKey` second-invocation
  // guard branch is similarly left untested for the same reason.
  useEffect(() => {
    if (loadingSshKeys || loadedDefaultSshKey || !vm) return
    const sshKeyPresent = (sshKeys as any[]).some((key) => key.name === vm?.key_name)
    if (sshKeyPresent) {
      updateParams({ sshKey: vm.key_name })
    }
    setLoadedDefaultSshKey(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingSshKeys, sshKeys, vm])

  const isClusterRequired = !(isVmware && isOpenCloud())

  return (
    <ModalForm
      route={cloneRoute}
      title="Clone Virtual Machine"
      onSubmit={() => handleClose(true)}
      // Step 5 is now the final step: the submit button only enables once the clone has actually
      // finished ('done'), not merely once it has stopped running — a failed clone must not leave
      // this the one click away from closing as if it had succeeded.
      disableSubmit={(step) => step === 5 && cloneStage !== 'done'}
      hideBack={cloneStage !== 'idle'}
      onClose={handleClose}
      error={responseError}
      // Step 4's "Next" click is the one that actually kicks off the clone (Step 5 starts the
      // snapshot/VM creation the instant it mounts) — labeling it "Submit" instead of "Next"
      // signals that real work begins here, not just a page turn.
      submitTitle={(step) => (step === 4 ? 'Submit' : step === 5 ? 'Finish' : 'Next')}
      maxWidth={900}
      steps={5}
      showConfirmationOnCancel
    >
      {({ step }) => (
        <>
          {step === 1 && (
            <CloneSourceStep
              vm={vm}
              existingSnapshotId={existingSnapshotId}
              setExistingSnapshotId={setExistingSnapshotId}
              existingSnapshots={existingSnapshots}
              loadingImages={loadingImages}
              loadingVolumeSnapshots={loadingVolumeSnapshots}
              volumeSnapshotsById={volumeSnapshotsById}
            />
          )}
          {step === 2 && (
            <CreateVirtualMachineFlavorStep
              {...flavorStep}
              params={params}
              updateParams={updateParams}
              hideCustomFlavorOption
            />
          )}
          {step === 3 && (
            <CreateVirtualMachineNetworkingStep
              {...networkingStep}
              getParamsUpdater={getParamsUpdater}
              params={params}
              setMinAvailableIpsNetwork={setMinAvailableIpsNetwork}
              updateParams={updateParams}
            />
          )}
          {step === 4 && (
            <div>
              <div className={classes.customizeStep}>
                <TextField
                  id="name"
                  label="Name"
                  onChange={getParamsUpdater('name')}
                  value={params.name}
                  info="Choose a name for your VM"
                  required
                />
                <PicklistField
                  id="availabilityZone"
                  label="Cluster"
                  info="Choose a cluster for your VM"
                  onChange={getParamsUpdater('availabilityZone')}
                  value={params.availabilityZone}
                  disableClusterWithoutHosts
                  fullWidth
                  required={isClusterRequired}
                  DropdownComponent={ClusterHostGroupsPicklist}
                />
              </div>
              <CreateVirtualMachineCustomizeStep
                {...customizeStep}
                getParamsUpdater={getParamsUpdater}
                getVolumeSnapshotSourceVolume={noop}
                minAvailableIpsNetwork={minAvailableIpsNetwork}
                params={params}
                updateParams={updateParams}
                vmNetworkMode={networkingStep.vmNetworkMode}
                hideInfoBanner
                hideVmCount
                hideServerGroup
                hideStretchedCluster
                hideQemuGuestAgent
                hideMigrationPriority
              />
            </div>
          )}
          {step === 5 && (
            <CloneProgressStep
              onStart={startClone}
              cloneProgressDetails={cloneProgressDetails}
              portCreationError={portCreationError}
              cloneError={cloneError}
              cloneStage={cloneStage}
              vmPassword={vmPassword}
              isWindowsVm={isWindowsVm}
              isExistingSnapshot={!!existingSnapshotId}
              snapshotDeleteError={snapshotDeleteError}
            />
          )}
        </>
      )}
    </ModalForm>
  )
}
